import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useCart } from './CartState';
import { getApiUrl, authHeaders, getTenantStorageKey } from '../utils/styleHelpers';

// Inline SVG Icon components (zero external dependencies)
const ShoppingBag = ({ size = 20, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
        <line x1="3" y1="6" x2="21" y2="6"></line>
        <path d="M16 10a4 4 0 0 1-8 0"></path>
    </svg>
);
const Search = ({ size = 20, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
);
const Package = ({ size = 20, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
        <line x1="12" y1="22.08" x2="12" y2="12"></line>
    </svg>
);
const Check = ({ size = 20, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
);
const RefreshCw = ({ size = 20, style = {}, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style} className={className}>
        <polyline points="23 4 23 10 17 10"></polyline>
        <polyline points="1 20 1 14 7 14"></polyline>
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
    </svg>
);
const X = ({ size = 20, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
);
const ArrowRight = ({ size = 20, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <line x1="5" y1="12" x2="19" y2="12"></line>
        <polyline points="12 5 19 12 12 19"></polyline>
    </svg>
);
const User = ({ size = 20, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
    </svg>
);
const Calendar = ({ size = 20, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
);
const ShoppingCart = ({ size = 20, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <circle cx="9" cy="21" r="1"></circle>
        <circle cx="20" cy="21" r="1"></circle>
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
    </svg>
);

export const CustomerOrdersModal = ({ isOpen, onClose, onNavigateToCart, config = {} }) => {
    const { addToCart } = useCart();
    const settings = config?.settings || {};

    const overlayZIndex = parseInt(settings.ordersModalZIndex || 9999999, 10);
    const overlayTopOffset = settings.ordersModalTopOffset || '90px';
    const overlayVerticalAlign = settings.ordersModalVerticalAlign || 'flex-start';
    const modalMaxWidth = settings.ordersModalMaxWidth || '720px';
    const modalMaxHeight = settings.ordersModalMaxHeight || 'calc(90vh - 60px)';
    const modalBgColor = settings.ordersModalBgColor || '#ffffff';
    const modalHeaderBg = settings.ordersModalHeaderBg || '#f8fafc';
    const modalTextColor = settings.ordersModalTextColor || '#0f172a';
    const modalBorderRadius = settings.ordersModalBorderRadius || '16px';
    const backdropBlur = settings.ordersModalBackdropBlur || '8px';
    const backdropBg = settings.ordersModalBackdropColor || 'rgba(15, 23, 42, 0.65)';
    const customCss = settings.ordersModalCustomCss || '';

    // Customer email and lookup state
    const storageKey = getTenantStorageKey('customer_email');
    const [customerEmail, setCustomerEmail] = useState('');
    const [emailInput, setEmailInput] = useState('');
    
    // Orders & loading state
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [reorderSuccessMsg, setReorderSuccessMsg] = useState('');

    // Load saved customer email from localStorage on mount
    useEffect(() => {
        if (typeof localStorage !== 'undefined') {
            const savedEmail = localStorage.getItem(storageKey);
            if (savedEmail) {
                setCustomerEmail(savedEmail);
                setEmailInput(savedEmail);
                fetchCustomerOrders(savedEmail);
            }
        }
    }, [storageKey, isOpen]);

    // Fetch orders for a specific email or query
    const fetchCustomerOrders = async (queryVal) => {
        const targetQuery = (queryVal || customerEmail || emailInput).trim();
        if (!targetQuery) return;

        setLoading(true);
        setErrorMsg('');
        setReorderSuccessMsg('');

        try {
            const url = getApiUrl(`api_ecommerce.php?action=order_lookup&q=${encodeURIComponent(targetQuery)}&t=${Date.now()}`);
            const res = await axios.get(url, authHeaders());

            if (res.data && res.data.success && Array.isArray(res.data.orders)) {
                setOrders(res.data.orders);
                if (res.data.orders.length === 0) {
                    setErrorMsg(`No orders found matching "${targetQuery}".`);
                } else if (targetQuery.includes('@')) {
                    // Save validated customer email
                    setCustomerEmail(targetQuery);
                    try { localStorage.setItem(storageKey, targetQuery); } catch (e) {}
                }
            } else {
                setOrders([]);
                setErrorMsg(res.data?.message || 'Order lookup failed.');
            }
        } catch (err) {
            console.error('Failed to fetch customer orders:', err);
            setErrorMsg('Unable to retrieve orders. Please check your connection.');
        } finally {
            setLoading(false);
        }
    };

    // Customer Login / Email Save
    const handleEmailSubmit = (e) => {
        e.preventDefault();
        if (!emailInput.trim()) return;
        fetchCustomerOrders(emailInput.trim());
    };

    // Logout customer / Clear saved email
    const handleLogout = () => {
        setCustomerEmail('');
        setEmailInput('');
        setOrders([]);
        setErrorMsg('');
        try { localStorage.removeItem(storageKey); } catch (e) {}
    };

    // 1-Click Reorder All Items from a Past Order
    const handleReorderOrder = (order) => {
        if (!order || !Array.isArray(order.items) || order.items.length === 0) return;

        let addedCount = 0;
        order.items.forEach(item => {
            const prodToInsert = {
                id: item.originalProductId || item.id || ('reorder_' + Date.now()),
                name: item.name || 'Ordered Product',
                price: parseFloat(item.price) || 0,
                images: item.images || (item.image ? [item.image] : []),
                category: item.category || 'Reorder',
                stock: 999,
                inquiryOnly: false
            };
            const qty = parseInt(item.quantity) || 1;
            addToCart(prodToInsert, qty, item.selectedAttributes || null, item.variation || null);
            addedCount += qty;
        });

        setReorderSuccessMsg(`Added ${addedCount} item(s) from Order #${order.id} to your cart!`);

        setTimeout(() => {
            if (onNavigateToCart) {
                onClose();
                onNavigateToCart();
            }
        }, 1200);
    };

    // Reorder Single Item from Past Order
    const handleReorderSingleItem = (item) => {
        const prodToInsert = {
            id: item.originalProductId || item.id || ('reorder_' + Date.now()),
            name: item.name || 'Ordered Product',
            price: parseFloat(item.price) || 0,
            images: item.images || (item.image ? [item.image] : []),
            category: item.category || 'Reorder',
            stock: 999,
            inquiryOnly: false
        };
        const qty = parseInt(item.quantity) || 1;
        addToCart(prodToInsert, qty, item.selectedAttributes || null, item.variation || null);
        setReorderSuccessMsg(`Added "${item.name}" to your cart!`);
    };

    if (!isOpen) return null;

    return createPortal(
        <div 
            className="customer-orders-modal-overlay"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: overlayZIndex,
                display: 'flex',
                alignItems: overlayVerticalAlign,
                justifyContent: 'center',
                background: backdropBg,
                backdropFilter: `blur(${backdropBlur})`,
                WebkitBackdropFilter: `blur(${backdropBlur})`,
                padding: '16px',
                paddingTop: overlayVerticalAlign === 'flex-start' ? overlayTopOffset : '16px',
                overflowY: 'auto'
            }}
        >
            {customCss && <style>{customCss}</style>}
            <div 
                className="customer-orders-modal-panel"
                style={{
                    background: modalBgColor,
                    color: modalTextColor,
                    borderRadius: modalBorderRadius,
                    width: '100%',
                    maxWidth: modalMaxWidth,
                    maxHeight: modalMaxHeight,
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
                    overflow: 'hidden',
                    marginTop: overlayVerticalAlign === 'flex-start' ? '0' : 'auto',
                    marginBottom: overlayVerticalAlign === 'flex-start' ? '0' : 'auto',
                    animation: 'modalSlideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
                }}
            >
                {/* Modal Header */}
                <div style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: modalHeaderBg
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: '#eff6ff',
                            color: '#2563eb',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Package size={22} />
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: '#0f172a' }}>
                                My Orders & Reorder
                            </h3>
                            <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#64748b' }}>
                                View your order history and reorder past items in 1-click
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '8px',
                            cursor: 'pointer',
                            color: '#64748b',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Modal Body */}
                <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
                    {/* Toast Notification */}
                    {reorderSuccessMsg && (
                        <div style={{
                            background: '#f0fdf4',
                            border: '1px solid #bbf7d0',
                            color: '#15803d',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontWeight: '600',
                            fontSize: '0.9rem'
                        }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Check size={18} /> {reorderSuccessMsg}
                            </span>
                            {onNavigateToCart && (
                                <button
                                    onClick={() => { onClose(); onNavigateToCart(); }}
                                    style={{
                                        background: '#15803d',
                                        color: 'white',
                                        border: 'none',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                >
                                    Go to Cart <ArrowRight size={14} />
                                </button>
                            )}
                        </div>
                    )}

                    {/* Login / Email Input Banner */}
                    <div style={{
                        background: '#f1f5f9',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        marginBottom: '24px'
                    }}>
                        {customerEmail ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <User size={18} style={{ color: '#2563eb' }} />
                                    <div>
                                        <span style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Customer Account</span>
                                        <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.95rem' }}>{customerEmail}</div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        onClick={() => fetchCustomerOrders(customerEmail)}
                                        style={{
                                            background: 'white',
                                            border: '1px solid #cbd5e1',
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            fontSize: '0.8rem',
                                            fontWeight: '600',
                                            color: '#334155',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                    >
                                        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
                                    </button>
                                    <button
                                        onClick={handleLogout}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            padding: '6px 10px',
                                            fontSize: '0.8rem',
                                            color: '#ef4444',
                                            cursor: 'pointer',
                                            fontWeight: '600'
                                        }}
                                    >
                                        Change Email
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155' }}>
                                    Enter your customer email or order number to view past orders:
                                </label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <input
                                        type="text"
                                        placeholder="e.g. customer@example.com or ORD-1001"
                                        value={emailInput}
                                        onChange={(e) => setEmailInput(e.target.value)}
                                        required
                                        style={{
                                            flex: 1,
                                            padding: '10px 14px',
                                            borderRadius: '8px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '0.9rem'
                                        }}
                                    />
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        style={{
                                            background: '#2563eb',
                                            color: 'white',
                                            border: 'none',
                                            padding: '10px 18px',
                                            borderRadius: '8px',
                                            fontWeight: '600',
                                            fontSize: '0.9rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        <Search size={16} /> Look Up
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Error Message */}
                    {errorMsg && (
                        <div style={{ padding: '16px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '0.88rem', marginBottom: '20px' }}>
                            {errorMsg}
                        </div>
                    )}

                    {/* Order History Cards */}
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                            <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: '10px' }} />
                            <div>Retrieving your order history...</div>
                        </div>
                    ) : orders.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {orders.map((ord) => {
                                const isShipped = Boolean(ord.shipped);
                                const isPaid = ord.status === 'paid';

                                return (
                                    <div key={ord.id} style={{
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        padding: '18px',
                                        background: '#ffffff',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                    }}>
                                        {/* Order Header */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px', marginBottom: '14px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                            <div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{ fontWeight: '800', fontSize: '1rem', color: '#0f172a' }}>
                                                        Order #{ord.id}
                                                    </span>
                                                    <span style={{
                                                        fontSize: '0.73rem',
                                                        fontWeight: '700',
                                                        padding: '2px 8px',
                                                        borderRadius: '12px',
                                                        background: isShipped ? '#f0fdf4' : isPaid ? '#eff6ff' : '#fffbeb',
                                                        color: isShipped ? '#15803d' : isPaid ? '#1d4ed8' : '#b45309',
                                                        border: `1px solid ${isShipped ? '#bbf7d0' : isPaid ? '#bfdbfe' : '#fde68a'}`
                                                    }}>
                                                        {isShipped ? '✓ Shipped' : (ord.status || 'Paid')}
                                                    </span>
                                                </div>
                                                <div style={{ fontSize: '0.78rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                                                    <Calendar size={13} /> {ord.date || 'Recent Order'}
                                                </div>
                                            </div>

                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontWeight: '800', fontSize: '1.1rem', color: '#0f172a' }}>
                                                    ${(parseFloat(ord.total) || 0).toFixed(2)}
                                                </div>
                                                <button
                                                    onClick={() => handleReorderOrder(ord)}
                                                    style={{
                                                        marginTop: '6px',
                                                        background: '#2563eb',
                                                        color: 'white',
                                                        border: 'none',
                                                        padding: '6px 14px',
                                                        borderRadius: '6px',
                                                        fontSize: '0.82rem',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        transition: 'background 0.15s ease'
                                                    }}
                                                >
                                                    <ShoppingCart size={14} /> Reorder All Items
                                                </button>
                                            </div>
                                        </div>

                                        {/* Purchased Items List */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {Array.isArray(ord.items) && ord.items.map((it, idx) => (
                                                <div key={idx} style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: '8px 12px',
                                                    background: '#f8fafc',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <div style={{ fontWeight: '600', color: '#0f172a' }}>
                                                            {it.name || 'Item'}
                                                        </div>
                                                        <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                                                            x{it.quantity || 1}
                                                        </span>
                                                    </div>

                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <span style={{ fontWeight: '700', color: '#334155' }}>
                                                            ${(parseFloat(it.price) || 0).toFixed(2)}
                                                        </span>
                                                        <button
                                                            onClick={() => handleReorderSingleItem(it)}
                                                            title="Reorder this single item"
                                                            style={{
                                                                background: 'white',
                                                                border: '1px solid #cbd5e1',
                                                                padding: '3px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '0.75rem',
                                                                fontWeight: '600',
                                                                color: '#2563eb',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            + Add
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b', background: '#f8fafc', borderRadius: '12px' }}>
                            <ShoppingBag size={32} style={{ color: '#94a3b8', marginBottom: '8px' }} />
                            <h4 style={{ margin: '0 0 4px', color: '#334155' }}>No Orders Found</h4>
                            <p style={{ margin: 0, fontSize: '0.85rem' }}>
                                Enter your customer email address above to view past purchases and reorder 1-click.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};
